<template>
    <div>
        <div class="card mb-4">
            <div class="card-header p-3">
                <div class="row align-items-center">
                    <div class="col-auto">
                        <span class="fe fe-x text-muted pointer" v-if="searchQuery" @click="searchQuery=null"></span>
                        <span class="fe fe-search text-muted" v-else></span>
                    </div>
                    <div class="col">
                        <b-form-input
                            v-model="searchQuery"
                            class="no-glow search form-control-flush"
                            :placeholder="$t('Search')"
                        ></b-form-input>
                    </div>
                </div>
                <ul class="nav nav-tabs nav-tabs-sm card-header-tabs">
                    <li class="nav-item">
                        <a
                            href="#"
                            class="nav-link text-center"
                            :class="{active:tab === 'PRESETS'}"
                            @click.prevent="tab='PRESETS'"
                        >{{ $t('Presets') }}</a>
                    </li>
                    <li class="nav-item">
                        <a
                            href="#"
                            class="nav-link text-center"
                            :class="{active:tab === 'SERVICES'}"
                            @click.prevent="tab='SERVICES'"
                        >{{ $t('Services') }}</a>
                    </li>
                </ul>
            </div>
        </div>
        <div class="row" v-if="tab === 'SERVICES'">
            <div class="col-4">
                <div class="card mb-2" @click="serviceId = 'recent'">
                    <div class="card-body p-2 hover pointer desk32-fz-md-14">
                        <button
                            class="btn btn-white btn-sm border-0"
                            v-if="serviceId === 'recent'"
                            @click.stop="serviceId = null"
                        >
                            <i class="fe fe-x"></i>
                        </button>
                        <span>{{ $t('Recently used') }}</span>
                    </div>
                </div>

                <div class="card mb-2" v-for="service in services" :key="service._id">
                    <div class="card-body p-2 pointer hover desk32-fz-md-14" @click="serviceId = service._id">
                        <button
                            v-if="serviceId === service._id"
                            @click.stop="serviceId = null"
                            class="btn btn-white btn-sm border-0"
                        >
                            <i class="fe fe-x"></i>
                        </button>
                        <span>{{ service.label }}<span class="text-muted"> ({{ service.totalTreatments }})</span></span>
                    </div>
                </div>
            </div>
            <div class="col-8">
                <list-loading :loading="areTreatmentsLoading" :count="treatments().length" search>
                    <div
                        class="card mb-2"
                        v-for="(treatment, treatmentIndex) in treatments()"
                        :key="treatmentIndex"
                    >
                        <div class="card-body pointer hover p-2 desk32-fz-md-14" @click="useTreatment(treatment)">
                            <div class="row">
                                <div class="col-12 col-sm">
                                <span>{{ treatment.label }}</span>
                            </div>
                                <div class="col-12 col-sm-auto text-right">
                                    <money :value="treatment.price || 0" class="text-right text-muted"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </list-loading>
            </div>
        </div>
        <div class="row" v-if="tab === 'PRESETS'">
            <div class="col-4">
                <div class="card mb-2" @click="serviceId = 'recent'">
                    <div class="card-body p-2 hover pointer desk32-fz-md-14">
                        <button
                            class="btn btn-white btn-sm border-0"
                            v-if="serviceId === 'recent'"
                            @click.stop="serviceId = null"
                        >
                            <i class="fe fe-x"></i>
                        </button>
                        <span>{{ $t('Recently used') }}</span>
                    </div>
                </div>

                <specialty-nav v-model="specialtyIds" type="div"/>
            </div>
            <div class="col-8">
                <list-loading :loading="arePresetsLoading" search :count="presets.length">
                    <div class="card mb-2" v-for="preset in presets" :key="preset._id">
                        <div class="card-body pointer hover p-2 desk32-fz-md-14" @click="usePreset(preset)">
                            <div class="row">
                                <div class="col">
                                <span>{{ preset.label }}</span>
                            </div>
                                <div class="col-auto"></div>
                            </div>
                        </div>
                    </div>
                </list-loading>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Money from "@/components/Money.vue";
import SpecialtyNav from '@/components/SpecialtyNav'
import debounce from "lodash/debounce";
import ListLoading from "@/components/Utils/ListLoading";

export default {
    name: "PresetTreatmentBrowser",
    props: {
        useTreatment: Function,
        usePreset: Function,
    },
    components: {
        ListLoading,
        Money,
        SpecialtyNav
    },
    data() {
        return {
            tab: 'SERVICES',
            treatmentQuery: {},
            specialtyIds: [],
            searchQuery: null,
            presetQuery: {},
            serviceId: null,
        }
    },
    computed: {
        ...mapState('auth', ['user']),
        ...mapGetters('service', { findServicesInStore: 'find' }),
        ...mapGetters('treatment', { findTreatmentInStore: 'find' }),
        ...mapGetters('presets', { findPresetInStore: 'find' }),
        ...mapState('presets', { arePresetsLoading: 'isFindPending' }),
        ...mapState('service', { areServicesLoading: 'isFindPending' }),
        ...mapState('treatment', { areTreatmentsLoading: 'isFindPending' }),

        services() {
            return this.findServicesInStore({ query: { $sort: { priority: 1 } } }).data;
        },
        presets() {
            return this.findPresetInStore({ query: this.presetQuery }).data;
        }
    },
    watch: {
        specialtyIds: debounce(function (newVal) {
            delete this.presetQuery.userId;
            delete this.presetQuery.specialty;
            delete this.presetQuery._id;

            if (newVal.length > 0) {
                if (newVal === 'my') {
                    this.presetQuery.userId = this.user._id;
                } else if (newVal === 'recent') {
                    this.presetQuery._id = { $in: this.user.recentPresets };
                } else {
                    this.presetQuery.specialty = { $in: newVal };
                }
            }

            this.fetchPresets({ query: this.presetQuery });
        }, 100),

        searchQuery: debounce(function (newVal) {
            if (this.tab === 'SERVICES')
                this.searchTreatments()
            else
                this.searchPresets()
        }, 300),

        serviceId(newVal) {
            delete this.treatmentQuery.serviceId;
            delete this.treatmentQuery._id;
            if (!newVal)
                return
            if (newVal !== 'recent') {
                this.treatmentQuery.serviceId = newVal;
                return
            }
            if (this.tab === 'PRESETS')
                this.presetQuery._id = { $in: this.user.recentPresets };
            else
                this.treatmentQuery._id = { $in: this.user.recentTreatments };
        }
    },
    methods: {
        ...mapActions('presets', { fetchPresets: 'find', createPreset: 'create' }),
        ...mapActions('treatment', { fetchTreatments: 'find' }),

        searchTreatments() {
            delete this.treatmentQuery.serviceId
            if (this.searchQuery) {
                delete this.treatmentQuery.$or;
                this.serviceId = null
                this.treatmentQuery.$or = [{
                    label: {
                        $regex: "(^|\\s)" + this.searchQuery,
                        $options: "i"
                    }
                }]
            } else {
                delete this.treatmentQuery.$or
            }
            this.fetchTreatments({ query: this.treatmentQuery });
        },

        searchPresets() {
            this.specialtyIds = []
            this.serviceId = null;

            if (this.searchQuery) {
                this.presetQuery.$or = [{
                    label: {
                        $regex: "(^|\\s)" + this.searchQuery,
                        $options: "i"
                    }
                }]
            } else {
                delete this.presetQuery.$or;
            }
            this.fetchPresets({ query: this.presetQuery });
        },

        treatments() {
            return this.findTreatmentInStore({
                query: {
                    _id: { $ne: 'temp' },
                    $sort: { code: 1 },
                    ...this.treatmentQuery
                }
            }).data;
        },
    },
    created() {
        this.fetchPresets()
    }
}
</script>

<style scoped>

</style>
